// /UI/Dropzone/hooks/useFileManagement.js
import { useState, useCallback, useMemo } from 'react';
import { VIDEO_LIMIT } from '../utils/constants';
import { processFile, cleanupFilePreviewUrls } from '../utils/helpers';

/**
 * Hook to manage files for the uploader
 * @param {Function} addNotification - Function to add notifications
 * @param {Function} throttledNotification - Function to add throttled notifications
 * @returns {Object} File management state and methods
 */
export const useFileManagement = (addNotification, throttledNotification) => {
  const [files, setFiles] = useState([]);
  const [isComplete, setIsComplete] = useState(false);

  // Memoized counts to avoid recalculation
  const videoCount = useMemo(() => files.filter(file => file.isVideo).length, [files]);
  const imageCount = useMemo(() => files.filter(file => !file.isVideo).length, [files]);
  const totalSize = useMemo(() => files.reduce((sum, file) => sum + file.size, 0), [files]);
  const fileCount = useMemo(() => files.length, [files]);

  /**
   * Handles the initial file drop and processes them
   */
  const handleFilesDrop = useCallback(
    async acceptedFiles => {
      try {
        const processedFiles = await Promise.all(
          acceptedFiles.map(async file => {
            const processedFile = await processFile(file);

            // Only check limits for videos
            if (processedFile.isVideo && videoCount + 1 > VIDEO_LIMIT) {
              throttledNotification(
                'warning',
                `Video limit (${VIDEO_LIMIT}) exceeded. Some videos weren't added.`
              );
              return null;
            }

            return processedFile;
          })
        );

        // Filter out any null values (rejected files)
        const validFiles = processedFiles.filter(file => file !== null);

        setFiles(prevFiles => [...prevFiles, ...validFiles]);

        if (validFiles.length > 0) {
          addNotification(
            'info',
            `${validFiles.length} file${validFiles.length !== 1 ? 's' : ''} added.`
          );
        }
      } catch (error) {
        console.error('Error processing files:', error);
        addNotification('error', 'Error processing files. Please try again.');
      }
    },
    [addNotification, throttledNotification, videoCount]
  );

  /**
   * Removes a file from the list
   */
  const removeFile = useCallback(id => {
    setFiles(currentFiles => {
      const fileToRemove = currentFiles.find(file => file.id === id);
      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }
      return currentFiles.filter(file => file.id !== id);
    });
  }, []);

  /**
   * Clears all files and resets the uploader
   */
  const clearAllFiles = useCallback(() => {
    // Clean up preview URLs
    cleanupFilePreviewUrls(files);

    setFiles([]);

    // Only reset upload status, but don't clear mediaFiles
    if (isComplete) {
      setIsComplete(false);
    }

    addNotification('info', 'All files cleared.');
  }, [files, isComplete, addNotification]);

  /**
   * Update a specific file in the files array
   */
  const updateFile = useCallback((id, updates) => {
    setFiles(currentFiles =>
      currentFiles.map(file => (file.id === id ? { ...file, ...updates } : file))
    );
  }, []);

  return {
    files,
    setFiles,
    isComplete,
    setIsComplete,
    videoCount,
    imageCount,
    totalSize,
    fileCount,
    handleFilesDrop,
    removeFile,
    clearAllFiles,
    updateFile,
  };
};
